/*
 * Model for KnowledgBases
 */

define([
  "backbone",
  "jquery",
  "underscore",
  "messaging/messenger",
  "configuration/properties",
  "modules/shared/sortableList/sortableListModel",
], function (Backbone, $, _, Messenger, Properties, SortableListModel) {
  var KbMenuModel = {};

  const ATTRIBUTE_DATA_TYPE = {
    STRING: 0,
    NUMBER: 1,
    BOOLEAN: 2,
  };

  KbMenuModel.ATTRIBUTE_DATA_TYPE = ATTRIBUTE_DATA_TYPE;

  KbMenuModel.kbModel = Backbone.Model.extend({
    idAttribute: "kbId",
    defaults: {
      kbName: "",
    },
  });

  KbMenuModel.kbCollection = Backbone.Collection.extend({
    model: KbMenuModel.kbModel,
    url: Properties.rootURL,
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "kbName");
    },
  });

  KbMenuModel.secretKeyModel = Backbone.Model.extend({
    idAttribute: "kbId",
    defaults: {
      secretKey: "",
    },
  });

  KbMenuModel.kbSecretKeyCollection = Backbone.Collection.extend({
    model: KbMenuModel.secretKeyModel,
    url: function () {
      return Properties.rootURL + "/secretKeys";
    },
  });

  KbMenuModel.standardResponseType = Backbone.Model.extend({
    idAttribute: "agentTransId",
    defaults: {},
  });

  KbMenuModel.standardResponsesType = Backbone.Collection.extend({
    model: KbMenuModel.standardResponseType,
    initializeCustom: function (options) {
      this.kbId = options.kbId;
      if (this.kbId) {
        this.fetch();
      }
    },
    url: function () {
      return Properties.rootURL + "/" + this.kbId + "/standard_responses_type/live";
    },
  });

  KbMenuModel.statuses = Backbone.Model.extend(
    {
      initializeCustom: function (options) {
        this.kbId = options.kbId;
        if (this.kbId) {
          this.fetch();
        }
      },
      parse: function (response) {
        let i;
        //go through each topic status and change the name to an i18n string
        for (i = 0; i < response.allTopicStatuses.length; i++) {
          let status = response.allTopicStatuses[i];
          status.statusName = KbMenuModel.statuses.getStatusName(status.statusId);
        }
        //go through each transition and add both "All" and "Selected" versions of the transition for a topic.
        //add a generic transition for response templates
        let newTopicTransitions = [];
        let newResponseTemplateTransitions = [];
        for (i = 0; i < response.allTopicStatusTransitions.length; ++i) {
          let originalTransition = response.allTopicStatusTransitions[i];
          switch (originalTransition.targetStatusId) {
            case KbMenuModel.statuses.NON_VERSIONED_ENABLED:
              newTopicTransitions.push(
                _.extend({}, originalTransition, {
                  transitionName: "topic.statuses.enableAll",
                }),
              );
              newTopicTransitions.push(
                _.extend({}, originalTransition, {
                  transitionName: "topic.statuses.enableSelected",
                }),
              );
              newResponseTemplateTransitions.push(
                _.extend({}, originalTransition, {
                  transitionName: "topic.statuses.enable",
                }),
              );
              break;
            case KbMenuModel.statuses.NON_VERSIONED_DISABLED:
            case KbMenuModel.statuses.VERSIONED_DISABLED:
              newTopicTransitions.push(
                _.extend({}, originalTransition, {
                  transitionName: "topic.statuses.disableAll",
                }),
              );
              newTopicTransitions.push(
                _.extend({}, originalTransition, {
                  transitionName: "topic.statuses.disableSelected",
                }),
              );
              newResponseTemplateTransitions.push(
                _.extend({}, originalTransition, {
                  transitionName: "topic.statuses.disable",
                }),
              );
              break;
            case KbMenuModel.statuses.VERSIONED_PUBLISHED:
              newTopicTransitions.push(
                _.extend({}, originalTransition, {
                  transitionName: "topic.statuses.publishAll",
                }),
              );
              newTopicTransitions.push(
                _.extend({}, originalTransition, {
                  transitionName: "topic.statuses.publishSelected",
                }),
              );
              newResponseTemplateTransitions.push(
                _.extend({}, originalTransition, {
                  transitionName: "topic.statuses.publish",
                }),
              );
              break;
            default:
              console.error(
                "Unexpected targetStatusId for transition: " + originalTransition.targetStatusId,
              );
              break;
          }
        }
        response.allTopicStatusTransitions = newTopicTransitions;
        response.allResponseTemplateStatusTransitions = newResponseTemplateTransitions;
        return response;
      },
      url: function () {
        return Properties.rootURL + "/" + this.kbId + "/topic_statuses";
      },
    },
    {
      NON_VERSIONED_CREATED: 6,
      NON_VERSIONED_ENABLED: 7,
      NON_VERSIONED_DISABLED: 8,
      VERSIONED_CREATED: 9,
      VERSIONED_PUBLISHED: 10,
      VERSIONED_PENDING_RESPONSE: 11,
      VERSIONED_DISABLED: 12,
      getStatusName: function (statusId) {
        switch (statusId) {
          case KbMenuModel.statuses.NON_VERSIONED_CREATED:
          case KbMenuModel.statuses.VERSIONED_CREATED:
            return $.t("topic.statuses.created");
          case KbMenuModel.statuses.NON_VERSIONED_ENABLED:
            return $.t("topic.statuses.enabled");
          case KbMenuModel.statuses.NON_VERSIONED_DISABLED:
          case KbMenuModel.statuses.VERSIONED_DISABLED:
            return $.t("topic.statuses.disabled");
          case KbMenuModel.statuses.VERSIONED_PENDING_RESPONSE:
            return $.t("topic.statuses.pendingResponse");
          case KbMenuModel.statuses.VERSIONED_PUBLISHED: {
            return $.t("topic.statuses.published");
          }
        }
      },
    },
  );

  KbMenuModel.contexts = Backbone.Collection.extend({
    initializeCustom: function (options) {
      this.kbId = options.kbId;
      if (this.kbId) {
        this.fetch();
      }
    },
    url: function () {
      return Properties.rootURL + "/" + this.kbId + "/contexts";
    },
  });

  KbMenuModel.touchpoint = Backbone.Model.extend({
    idAttribute: "touchPointId",
  });

  KbMenuModel.touchpoints = Backbone.Collection.extend({
    model: KbMenuModel.touchpoint,
    initializeCustom: function (options) {
      this.kbId = options.kbId;
      if (this.kbId) {
        this.fetch();
      }
    },
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "touchPoint");
    },
    url: function () {
      return Properties.rootURL + "/" + this.kbId + "/touchpoints";
    },
  });

  KbMenuModel.language = Backbone.Model.extend({
    idAttribute: "id",
  });

  KbMenuModel.languages = Backbone.Collection.extend({
    model: KbMenuModel.language,
    initializeCustom: function (options) {
      this.kbId = options.kbId;
      if (this.kbId) {
        this.fetch();
      }
    },
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "name");
    },
    url: function () {
      return Properties.rootURL + "/" + this.kbId + "/languages";
    },
  });

  KbMenuModel.languagesWithDefault = Backbone.Collection.extend({
    initializeCustom: function (options) {
      this.kbId = options.kbId;
      if (this.kbId) {
        this.fetch();
      }
    },
    comparator: function (a, b) {
      // Need to make sure that "Default" language is before everything else.
      // The Default language should have an ID of 0.
      var aId = a.get("id");
      var bId = b.get("id");
      if (typeof aId == "number" && typeof bId == "number") {
        if (aId === 0 && bId === 0) {
          return 0;
        }
        if (aId === 0) {
          return -1;
        }
        if (bId === 0) {
          return 1;
        }
      }
      return Backbone.textComparator(a, b, "name");
    },
    url: function () {
      return Properties.rootURL + "/" + this.kbId + "/languagesWithDefault";
    },
  });

  KbMenuModel.categoryValueTopicUsage = Backbone.Model.extend({
    idAttribute: "categoryValueId",
    url: function () {
      return (
        Properties.rootURL +
        "/" +
        this.get("kbId") +
        "/categories/" +
        this.get("categoryId") +
        "/values/" +
        this.get("categoryValueId") +
        "/topicUsage"
      );
    },
  });

  KbMenuModel.categoryValue = Backbone.Model.extend({
    idAttribute: "categoryValueId",
    defaults: {
      value: "",
      description: "",
      localizedCategoryValues: [],
    },

    parse: function (response) {
      if (!response) {
        return response;
      }
      // Flatten the localized values to a series of "localizedValue_LANGUAGE_ID" values
      var localizedCategoryValues = response.localizedCategoryValues;
      if (!localizedCategoryValues || localizedCategoryValues.length === 0) {
        return response;
      }
      for (var i = 0; i < localizedCategoryValues.length; ++i) {
        var localizedCategoryValue = localizedCategoryValues[i];
        response["localizedValue_" + localizedCategoryValue.languageId] =
          localizedCategoryValue.value;
      }
      return response;
    },

    toJSON: function (options) {
      if (options && (options.parse || options.saving)) {
        var json = {};
        var attributes = this.attributes;
        var localizedValueRe = /localizedValue_/i;
        var localizedCategoryValues = attributes.localizedCategoryValues;
        var localizedValueMap = {};
        if (localizedCategoryValues) {
          for (var i = 0; i < localizedCategoryValues.length; ++i) {
            localizedValueMap[localizedCategoryValues[i].languageId] = localizedCategoryValues[i];
          }
        }
        localizedCategoryValues = [];
        for (var prop in attributes) {
          if (
            Object.prototype.hasOwnProperty.call(attributes, prop) &&
            prop !== "localizedCategoryValues"
          ) {
            var value = attributes[prop];
            if (localizedValueRe.test(prop)) {
              if (value) {
                var languageId = parseInt(prop.substring(15), 10);
                var localizedCategoryValue = localizedValueMap[languageId];
                if (localizedCategoryValue) {
                  localizedCategoryValue.value = value;
                } else {
                  localizedCategoryValue = {
                    categoryMappingId: attributes.categoryValueId,
                    languageId: languageId,
                    value: value,
                  };
                }
                localizedCategoryValues.push(localizedCategoryValue);
              }
            } else {
              json[prop] = value;
            }
          }
        }
        json.localizedCategoryValues = localizedCategoryValues;
        return json;
      }
      return this.constructor.__super__.toJSON.apply(this, arguments);
    },

    validateAttribute: function (attribute, value) {
      if (attribute === "value") {
        if (!value || value.length === 0) {
          return $.t("admin.categories.values.noValue");
        }
      }
      if (attribute === "value" || /localizedValue_/.test(attribute)) {
        if (value.length > 255) {
          return $.t("commonMessages.tooLong", { number: 255, current: value.length });
        }

        // if the value duplicates an existing value then fail validation
        const parentCollection = this.parentCollection;
        if (parentCollection) {
          for (let i = 0; i < parentCollection.length; i++) {
            const modelAttrs = parentCollection.at(i).attributes;
            // skip the check if we are comparing to ourselves.
            // Category values should use case insensitive comparison.
            if (
              this.attributes !== modelAttrs &&
              value.toLowerCase() === modelAttrs.value.toLowerCase()
            ) {
              return $.t("admin.categories.values.duplicateValue", { categoryValueName: value });
            }
          }
        }
      }
    },
  });

  KbMenuModel.categoryValues = Backbone.Collection.extend({
    model: KbMenuModel.categoryValue,
    addNewValue: function () {
      var newValue = new KbMenuModel.categoryValue();
      // store a reference to my parent collection so it can be used during validation
      newValue.parentCollection = this;
      this.add(newValue);
      return newValue;
    },
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "value");
    },
    parse: function (models, options) {
      if (!models || !models.length) {
        return models;
      }
      var result = [];
      options = _.extend({ parse: true }, options);
      for (var i = 0; i < models.length; ++i) {
        result.push(new KbMenuModel.categoryValue(models[i], options));
      }
      return result;
    },
    url: function () {
      if (!this.category) {
        return null;
      }
      var url = _.result(this.category, "url");
      if (url[url.length - 1] === "/") {
        return url + "values";
      }
      return url + "/values";
    },
  });

  KbMenuModel.categoryNameLocalized = Backbone.Model.extend({
    defaults: {
      name: "",
    },
    parse: function (response) {
      return response;
    },

    toJSON: function () {
      return this.constructor.__super__.toJSON.apply(this, arguments);
    },
  });

  KbMenuModel.categoryNamesLocalized = Backbone.Collection.extend({
    model: KbMenuModel.categoryNameLocalized,
    addNewValue: function () {
      var newValue = new KbMenuModel.categoryNameLocalized();
      // store a reference to my parent collection so it can be used during validation
      newValue.parentCollection = this;
      this.add(newValue);
      return newValue;
    },
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "value");
    },
    parse: function (models, options) {
      if (!models || !models.length) {
        return models;
      }
      var result = [];
      options = _.extend({ parse: true }, options);
      for (var i = 0; i < models.length; ++i) {
        result.push(new KbMenuModel.categoryNameLocalized(models[i], options));
      }
      return result;
    },
  });

  KbMenuModel.category = Backbone.Model.extend({
    idAttribute: "categoryId",
    defaults: function () {
      var defaults = {
        categoryName: "",
        categoryNamesLocalized: new KbMenuModel.categoryNamesLocalized(),
        description: "",
        unfilterable: false,
      };
      return defaults;
    },
    parse: function (response) {
      var i;
      if (response.categoryValues) {
        var values = this.getValues();
        values.reset([]);
        for (i = 0; i < response.categoryValues.length; i++) {
          var categoryValue = new KbMenuModel.categoryValue(response.categoryValues[i], {
            parse: true,
          });
          // store a reference to my parent collection so it can be used during validation
          categoryValue.parentCollection = values;
          values.add(categoryValue);
        }
        delete response.categoryValues;
      }
      var names = new KbMenuModel.categoryNamesLocalized();
      for (i = 0; i < response.categoryNamesLocalized.length; i++) {
        var categoryNameLocalized = new KbMenuModel.categoryNameLocalized(
          response.categoryNamesLocalized[i],
          { parse: false },
        );
        // store a reference to my parent collection so it can be used during validation
        categoryNameLocalized.parentCollection = names;
        names.add(categoryNameLocalized);
      }
      response.categoryNamesLocalized = names;
      return response;
    },
    toJSON: function (options) {
      options = options || {};
      var json = this.constructor.__super__.toJSON.call(this, options);
      if (!options.saving && !options.parse) {
        return json;
      }
      var categoryNamesLocalized = this.get("categoryNamesLocalized").toJSON(options);
      var categoryNamesLocalizedNew = [];
      if (options && (options.saving || options.parse)) {
        categoryNamesLocalized.forEach(function (nameLocalized) {
          if (nameLocalized.name !== "" || nameLocalized.name.length !== 0) {
            categoryNamesLocalizedNew.push(nameLocalized);
          }
        });
      }
      json.categoryNamesLocalized = categoryNamesLocalizedNew;
      return json;
    },
    validateAttribute: function (attribute, value, allAttributes) {
      if (attribute === "categoryName") {
        if (!value) {
          return $.t("admin.categories.noName");
        }
        if (value.length > 255) {
          return $.t("commonMessages.tooLong", {
            number: 255,
            current: value.length,
          });
        }
        // Validate that category name is unique amongst categories & attributes
        // Category
        for (let i = 0; i < this.collection.length; i++) {
          // validate against free text attribute names
          const otherAttribute = this.collection.at(i);
          if (this !== otherAttribute) {
            if (
              value.toLocaleUpperCase() === otherAttribute.get("categoryName").toLocaleUpperCase()
            ) {
              return $.t("admin.categories.duplicateName");
            }
          }
        }

        // Attribute
        const kbAttributes = Messenger.reqres.request("kb:attributes");
        const thisCategoryId = allAttributes ? allAttributes.categoryId : this.get("categoryId");
        for (let i = 0; i < kbAttributes.length; i++) {
          var attr = kbAttributes.at(i);
          // Check name against other attributes.  Category ID is attribute ID
          // noinspection EqualityComparisonWithCoercionJS
          if (attr.get("id") != thisCategoryId) {
            if (attr.get("name").toLocaleUpperCase() === value.toLocaleUpperCase()) {
              return $.t("admin.categories.duplicateName");
            }
          }
        }
      }
    },
    validate: function () {
      var error = {};
      var isValid = true;
      //validate category name
      var message = this.validateAttribute("categoryName", this.get("categoryName"));
      if (message) {
        error["categoryName"] = message;
        isValid = false;
      }
      if (!isValid) {
        return error;
      }
    },
    getValues: function () {
      // I know this is not how most submodels work.  But, JavaScript already exists that expects the
      // Category Values collection to be in the model attributes.  So, it was easiest to just make it
      // continue to work that way.
      // Do not try to use getSubModel to create the collection, it can lead to the values being
      // saved multiple times.
      this.categoryValuesCollection = this.getSubModel(
        this.categoryValuesCollection,
        KbMenuModel.categoryValues,
        "/values",
        true,
        true,
      );
      this.categoryValuesCollection.category = this;
      return this.categoryValuesCollection;
    },
    getNamesLocalized: function () {
      return this.get("categoryNamesLocalized");
    },
    saveChildren: function (options) {
      this.saveSubModels(options);
    },
    _allowSave: function () {
      return (
        this.constructor.__super__._allowSave.apply(this, arguments) ||
        this.get("categoryNamesLocalized").any(function (categoryNameLocalized) {
          return categoryNameLocalized.dirty || categoryNameLocalized.isNew();
        })
      );
    },
  });

  KbMenuModel.categories = Backbone.Collection.extend(
    {
      model: KbMenuModel.category,
      addCategory: function () {
        var category = new KbMenuModel.category();
        this.add(category);
        return category;
      },
      comparator: function (a, b) {
        return Backbone.textComparator(a, b, "categoryName");
      },
      initializeCustom: function (options) {
        this.kbId = options.kbId;
        this.on("sync", function () {
          this.sort();
        });
        if (this.kbId) {
          this.fetch();
        }
      },
      parse: function (models, options) {
        if (!models || !models.length) {
          return models;
        }
        var result = [];
        options = _.extend({ parse: true }, options);
        for (var i = 0; i < models.length; ++i) {
          result.push(new KbMenuModel.category(models[i], options));
        }
        return result;
      },
      url: function () {
        return Properties.rootURL + "/" + this.kbId + "/categories";
      },
    },
    {
      /**
       * Extract category inserts from topic response
       *    for each category insert
       *        extract category id
       *            compute csv from category values *** Only CSV supported as of yet
       */
      translateCategoryForResponse: function (
        responseString,
        responseId,
        languageId,
        topicCategories,
        allCategories,
        showLabel,
      ) {
        if (!responseString) {
          return responseString;
        }

        const getCategoriesRegex = new RegExp(
          '<span class="categoryinsert"[^>]*>(.*?)</span>',
          "mg",
        );
        const categoryIdRegex = /data-akcategory="(\d+)"/;

        return responseString.replace(getCategoriesRegex, (categoryInResponse, categoryName) => {
          try {
            const categoryId = categoryInResponse.match(categoryIdRegex)[1];
            return this.translateToCSV(
              categoryId,
              categoryName,
              languageId,
              topicCategories,
              allCategories,
              showLabel,
            );
          } catch (e) {
            // Sometimes the database has bad data. Catch any problems parsing
            // the category or retrieving the category values and just use
            // a blank string
            if (showLabel) {
              return this.getLabel(categoryName);
            }
            return "";
          }
        });
      },

      translateToCSV: function (
        categoryId,
        categoryName,
        languageId,
        topicCategories,
        allCategories,
        showLabel,
      ) {
        const filteredCategoryValues = [];
        const formatResult = () => {
          if (showLabel) {
            return this.getLabel(categoryName) + filteredCategoryValues.join(", ");
          } else {
            return filteredCategoryValues.join(", ");
          }
        };
        if (!categoryId || !topicCategories || !allCategories) {
          return formatResult();
        }
        if (typeof categoryId !== "number") {
          categoryId = parseInt(categoryId, 10);
        }
        const category = allCategories.where({ categoryId: categoryId }, true);
        const filteredTopicCategories = topicCategories.where({ categoryId: categoryId });
        if (!category || !filteredTopicCategories || !filteredTopicCategories.length) {
          return formatResult();
        }
        const categoryValues = category.getValues();
        _.forEach(filteredTopicCategories, function (categoryValueBinding) {
          let categoryValueString = categoryValueBinding.get("categoryValue");
          if (languageId) {
            const categoryValueId = categoryValueBinding.get("categoryValueId");
            const categoryValueObject = categoryValues.where(
              { categoryValueId: categoryValueId },
              true,
            );
            if (categoryValueObject) {
              const localizedCategoryValueString = categoryValueObject.get(
                "localizedValue_" + languageId,
              );
              if (
                !_.isUndefined(localizedCategoryValueString) &&
                localizedCategoryValueString !== null
              ) {
                categoryValueString = localizedCategoryValueString;
              }
            }
          }
          filteredCategoryValues.push(categoryValueString);
        });
        return formatResult();
      },

      getLabel: function (categoryName) {
        return _.unescape(
          '<button class="btn btn-mini attribute-label btn-info" title="' +
            categoryName +
            '" data-toggle="tooltip" data-i18n="topic.tooltip.categoryLabel" data-toggle="tooltip"></button>',
        );
      },
    },
  );

  KbMenuModel.agents = Backbone.Collection.extend({
    initializeCustom: function (options) {
      this.kbId = options.kbId;
      if (this.kbId) {
        this.fetch();
      }
      this.on("sync", function () {
        this.sort();
      });
    },
    comparator: function (one, two) {
      return Backbone.textComparator(one, two, "name");
    },
    url: function () {
      return Properties.rootURL + "/" + this.kbId + "/mart/agents";
    },
  });

  KbMenuModel.stopWords = Backbone.Collection.extend({
    initializeCustom: function () {
      this.fetch();
    },
    url: function () {
      return Properties.rootURL + "/stopwords";
    },
  });

  KbMenuModel.martStatus = Backbone.Model.extend({
    idAttribute: "id",
    defaults: {
      value: "",
    },
    defaultValidateAttributes: ["value"],
    parse: function (response) {
      //if the status is New, Completed, Flagged, or Junk, then mark the model as 'readOnly'
      if (
        response.value === "New" ||
        response.value === "Completed" ||
        response.value === "Flagged" ||
        response.value === "Junk"
      ) {
        this.readOnly = true;
      }
      return response;
    },
    _validateDuplicateMartStatus: function (value) {
      //status must be unique in the group
      for (let i = 0; i < this.collection.length; i++) {
        const status = this.collection.at(i);
        if (this !== status) {
          //don't check the entry against itself
          if (value.toUpperCase() === status.get("value").toUpperCase()) {
            return $.t("admin.learnerStatusValidationError");
          }
        }
      }
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "value") {
        return (
          this.validateRequiredValue(value, { maxLength: 50 }) ||
          this.validateSafeCharacters(value) ||
          this._validateDuplicateMartStatus(value)
        );
      }
    },
  });

  KbMenuModel.martStatuses = Backbone.Collection.extend({
    comparator: "id",
    model: KbMenuModel.martStatus,
    addItem: function () {
      var newItem = new KbMenuModel.martStatus();
      this.add(newItem);
      return newItem;
    },
    initializeCustom: function (options) {
      //fetch only if a kbId is provided.  This prevents a bad fetch from occuring when this collection is clonsed.
      if (options.kbId) {
        this.kbId = options.kbId;
        if (this.kbId) {
          this.fetch();
        }
      }
    },
    url: function () {
      return Properties.rootURL + "/" + this.kbId + "/mart/authoring_statuses";
    },
  });

  KbMenuModel.privilegedAreaRole = Backbone.Model.extend({
    idAttribute: "roleId",
    defaults: {
      roleName: "",
      roleDescription: "",
    },
    defaultValidateAttributes: ["roleName", "roleDescription"],
    _validateDuplicateRoleName: function (value) {
      //role must be unique in the group
      for (let i = 0; i < this.collection.length; i++) {
        const role = this.collection.at(i);
        if (this !== role) {
          //don't check the entry against itself
          if (value.toUpperCase() === role.get("roleName").toUpperCase()) {
            return $.t("admin.privilegedAreaRoleValidationError");
          }
        }
      }
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "roleName") {
        return (
          this.validateRequiredValue(value, { maxLength: 100 }) ||
          this.validateSafeCharacters(value) ||
          this._validateDuplicateRoleName(value)
        );
      }
      if (attribute === "roleDescription") {
        return this.validateStringLength(value, { maxLength: 1000 });
      }
    },
  });

  KbMenuModel.privilegedAreaRoles = Backbone.Collection.extend({
    model: KbMenuModel.privilegedAreaRole,
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "roleName");
    },
    addItem: function () {
      var newItem = new KbMenuModel.privilegedAreaRole();
      this.add(newItem);
      return newItem;
    },
    initializeCustom: function (options) {
      this.kbId = options.kbId;
      if (this.kbId) {
        this.fetch();
      }
    },
    url: function () {
      return Properties.rootURL + "/" + this.kbId + "/privileged_area/roles";
    },
  });

  KbMenuModel.contentAwarenessIgnoredUrl = Backbone.Model.extend({
    idAttribute: "id",
    defaults: {
      value: "",
    },
    defaultValidateAttributes: ["value"],
    _validateDuplicateIgnoredUrl: function (value) {
      for (let i = 0; i < this.collection.length; i++) {
        const status = this.collection.at(i);
        if (this !== status) {
          //don't check the entry against itself
          if (value.toUpperCase() === status.get("value").toUpperCase()) {
            return $.t("admin.contentAwarenessValidationError");
          }
        }
      }
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "value") {
        return (
          this.validateRequiredValue(value, { maxLength: 500 }) ||
          this._validateDuplicateIgnoredUrl(value)
        );
      }
    },
  });

  KbMenuModel.contentAwarenessIgnoredUrls = Backbone.Collection.extend({
    model: KbMenuModel.contentAwarenessIgnoredUrl,
    addItem: function () {
      var newItem = new KbMenuModel.contentAwarenessIgnoredUrl();
      this.add(newItem);
      return newItem;
    },
    initializeCustom: function (options) {
      this.kbId = options.kbId;
      this.fetch();
    },
    url: function () {
      return Properties.rootURL + "/" + this.kbId + "/contentAwarenessIgnoredUrls";
    },
  });

  KbMenuModel.convertBackslash = Backbone.Model.extend({
    idAttribute: "kbId", //set it as something so that we save using PUT instead of POST
    defaults: {
      kbId: 1031,
      convertBackslash: false,
    },
    url: function () {
      var kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/convertBackslash";
    },
  });

  KbMenuModel.defaultUrl = Backbone.Model.extend({
    idAttribute: "defaultUrl", //set it as something so that we save using PUT instead of POST
    defaults: {
      defaultUrl: "",
    },
    defaultValidateAttributes: ["defaultUrl"],
    initializeCustom: function (options) {
      this.kbId = options.kbId;
      this.fetch();
    },
    url: function () {
      return Properties.rootURL + "/" + this.kbId + "/defaultUrl";
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "defaultUrl") {
        return this.validateStringLength(value, { maxLength: 500 });
      }
    },
  });

  Messenger.reqres.setHandler("kbs", function () {
    var kbs = new KbMenuModel.kbCollection();
    kbs.fetch();
    kbs.on("sync", function () {
      this.sort();
    });
    return kbs;
  });

  Messenger.reqres.setHandler("kbSecretKeys", function () {
    var kbSecretKeys = new KbMenuModel.kbSecretKeyCollection();
    kbSecretKeys.fetch();
    return kbSecretKeys;
  });

  KbMenuModel.attributeType = Backbone.Model.extend({});

  KbMenuModel.attributeTypes = Backbone.Collection.extend({
    model: KbMenuModel.attributeType,

    initializeCustom: function (options) {
      this.kbId = options.kbId;
      this.fetch();
    },

    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "type");
    },

    url: function () {
      return Properties.adminRootURL + "/kbs/" + this.kbId + "/attributeTypes";
    },
  });

  KbMenuModel.attribute = Backbone.Model.extend({
    defaults: {
      unfilterable: false,
    },
    defaultValidateAttributes: ["name"],
    _validateDuplicateAttributeName: function (value) {
      for (let i = 0; i < this.collection.length; i++) {
        // validate against free text attribute names
        const otherAttribute = this.collection.at(i);
        if (this !== otherAttribute) {
          if (value.toLocaleUpperCase() === otherAttribute.get("name").toLocaleUpperCase()) {
            return $.t("admin.attributes.duplicateAttributeNameError");
          }
        }
      }
    },
    _validateDuplicateCategoryName: function (value) {
      const kbCategories = Messenger.reqres.request("kb:categories");
      for (let i = 0; i < kbCategories.length; i++) {
        // validate against free text attribute names
        const otherCategory = kbCategories.at(i);
        if (this !== otherCategory) {
          if (value.toLocaleUpperCase() === otherCategory.get("categoryName").toLocaleUpperCase()) {
            return $.t("admin.attributes.duplicateAttributeNameError");
          }
        }
      }
    },
    validateAttribute: function (attribute, value) {
      // noinspection EqualityComparisonWithCoercionJS
      if (attribute == "name") {
        return (
          this.validateRequiredValue(value, { maxLength: 50 }) ||
          this.validateSafeCharacters(value) ||
          this._validateDuplicateAttributeName(value) ||
          this._validateDuplicateCategoryName(value)
        );
      }
    },

    backup: function () {
      this.onSynced(function () {
        this.wasDirty = this.dirty;
        this.cloneCopy = this.clone();
      }, this);
    },

    restore: function () {
      this.set(this.cloneCopy.attributes);
      this.dirty = this.wasDirty;
    },
  });

  KbMenuModel.attributes = Backbone.Collection.extend(
    {
      model: KbMenuModel.attribute,

      initializeCustom: function (options) {
        this.kbId = options.kbId;
        if (this.kbId) {
          this.fetch();
        }
      },

      comparator: function (a, b) {
        return Backbone.textComparator(a, b, "name");
      },

      addItem: function () {
        var newAttribute = new KbMenuModel.attribute();
        this.add(newAttribute);
        return newAttribute;
      },

      url: function () {
        return Properties.adminRootURL + "/kbs/" + this.kbId + "/attributes";
      },
    },
    {
      translateAttributesForResponse: function (
        responseString,
        responseId,
        languageId,
        attributes,
        showLabel,
      ) {
        // Regex's to get content
        const attributesRegex = new RegExp('<span class="attributeinsert"[^>]*>(.*?)</span>', "mg");
        const attributeIdRegex = /data-akattribute="(\d+)"/;
        const attributeInsertTypeRegex = /data-akinserttype="(.*?)"/;

        return responseString.replace(attributesRegex, (attributeInResponse, attributeName) => {
          try {
            const attributeId = attributeInResponse.match(attributeIdRegex)[1];
            const insertType = attributeInResponse.match(attributeInsertTypeRegex)[1];
            const attribute = attributes.find(function (attr) {
              // jshint ignore:line
              // noinspection JSReferencingMutableVariableFromClosure,EqualityComparisonWithCoercionJS
              return attr.get("attributeId") == attributeId;
            });

            let replacement = this.replacementValue(
              attribute,
              attributeName,
              insertType,
              languageId,
            );
            if (showLabel) {
              replacement = this.getLabel(attributeName) + replacement;
            }
            return replacement;
          } catch (e) {
            // Sometimes the database has bad data. Catch any problems parsing
            // the attribute or retrieving the attribute value and just use
            // a blank string
            if (showLabel) {
              return this.getLabel(attributeName);
            }
            return "";
          }
        });
      },

      getLabel: function (attributeName) {
        return _.unescape(
          '<button class="btn btn-mini attribute-label btn-danger" title="' +
            attributeName +
            '" data-toggle="tooltip" data-i18n="topic.tooltip.attributeLabel"></button>',
        );
      },

      replacementValue: function (attribute, attributeName, insertType, languageId) {
        if (!attribute) {
          return "";
        }

        let attributeValue = attribute.get("attributeValue");
        if (languageId) {
          const localizedAttributeValue = attribute.get("localizedAttributeValue_" + languageId);
          if (!_.isUndefined(localizedAttributeValue) && localizedAttributeValue !== null) {
            attributeValue = localizedAttributeValue;
          }
        }
        if (_.isUndefined(attributeValue) || attributeValue === null) {
          attributeValue = "";
        }
        switch (insertType) {
          case "text":
            return attributeValue;
          case "image":
            return _.unescape(`<img src="${attributeValue}" alt="${attributeName || ""}"/>`);
          case "link":
            return _.unescape(`<a href="${attributeValue}">${attributeValue}</a>`);
          default:
            return _.unescape(attributeValue);
        }
      },
    },
  );

  //request handlers for fetching this data outside of the KB menu

  Messenger.reqres.setHandler("fetch:kb:touchpoints", function (kbId) {
    return new KbMenuModel.touchpoints({ kbId: kbId });
  });

  Messenger.reqres.setHandler("fetch:kb:statuses", function (kbId) {
    return new KbMenuModel.statuses({ kbId: kbId });
  });

  Messenger.reqres.setHandler("fetch:kb:contexts", function (kbId) {
    return new KbMenuModel.contexts({ kbId: kbId });
  });

  Messenger.reqres.setHandler("fetch:kb:languages", function (kbId) {
    return new KbMenuModel.languages({ kbId: kbId });
  });

  Messenger.reqres.setHandler("fetch:kb:languagesWithDefault", function (kbId) {
    return new KbMenuModel.languagesWithDefault({ kbId: kbId });
  });

  Messenger.reqres.setHandler("fetch:kb:attributes", function (kbId) {
    return new KbMenuModel.attributes({ kbId: kbId });
  });

  Messenger.reqres.setHandler("fetch:kb:attributeTypes", function (kbId) {
    return new KbMenuModel.attributeTypes({ kbId: kbId });
  });

  Messenger.reqres.setHandler(
    "fetch:kb:categoryValue:topicUsage",
    function (kbId, categoryId, categoryValueId) {
      let categoryValueTopicUsage = new KbMenuModel.categoryValueTopicUsage({
        kbId: kbId,
        categoryId: categoryId,
        categoryValueId: categoryValueId,
      });
      categoryValueTopicUsage.fetch();
      return categoryValueTopicUsage;
    },
  );

  Messenger.reqres.setHandler("kb:attributes:list:headers", function () {
    let Header = SortableListModel.headerModel;
    return new Backbone.Collection([
      new Header({
        headerName: $.t("admin.attributes.name"),
        modelAttribute: "name",
        selectable: true,
        sortable: true,
        filterable: false,
      }),
      new Header({
        headerName: "",
        modelAttribute: "name",
        width: "15px",
        customHtml:
          '<button type="button" class="btn btn-danger btn-mini deleteAttribute pull-right" data-i18n="[title]admin.attributes.deleteAttribute"> <i class="icon-remove"></i></button>',
      }),
    ]);
  });

  Messenger.reqres.setHandler("fetch:attribute:dataTypes", function () {
    const dataTypes = [
      { id: ATTRIBUTE_DATA_TYPE.STRING, type: $.t("admin.attributes.stringDataType") },
      { id: ATTRIBUTE_DATA_TYPE.NUMBER, type: $.t("admin.attributes.numberDataType") },
      { id: ATTRIBUTE_DATA_TYPE.BOOLEAN, type: $.t("admin.attributes.booleanDataType") },
    ];
    return new Backbone.Collection(dataTypes);
  });

  return KbMenuModel;
});
